import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  Input,
  HostListener,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ApiService } from 'src/app/core/providers/api/api.service';

@Component({
  selector: 'app-mention-textarea',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './mention-textarea.component.html',
  styleUrl: './mention-textarea.component.scss',
})
export class MentionTextareaComponent {
  @ViewChild('textarea') textarea!: ElementRef<HTMLDivElement>;
  @Output() contentChange = new EventEmitter<string>();
  @Output() mentionsChange = new EventEmitter<any[]>(); // Nouvel output pour les mentions
  @Input() placeholder: string = 'Votre commentaire...';

  userSearchResults: any[] = [];
  showUserDropdown: boolean = false;
  userSearchLoading: boolean = false;
  lastAtPosition: number = -1;
  content: string = '';
  selectedUserIndex: number = -1;

  constructor(private apiService: ApiService) {}

  onInput(event: any): void {
    this.content = this.textarea.nativeElement.innerHTML;
    this.contentChange.emit(this.content);

    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;
    const textContent = textNode.textContent || '';
    const cursorPos = range.startOffset;

    if (textContent[cursorPos - 1] === '@') {
      this.lastAtPosition = cursorPos - 1;
      this.showUserDropdown = true;
      this.selectedUserIndex = -1;
      this.searchUsers('');
    } else if (
      textContent.substring(this.lastAtPosition, cursorPos).includes(' ')
    ) {
      this.showUserDropdown = false;
      this.lastAtPosition = -1;
    } else if (this.showUserDropdown) {
      const searchTerm = textContent.substring(
        this.lastAtPosition + 1,
        cursorPos
      );
      this.searchUsers(searchTerm);
    }
  }

  searchUsers(keyword: string): void {
    this.userSearchLoading = true;
    this.apiService
      .getAllPages('user_firms', {
        search: keyword,
        'userInfo.isEnabled': true,
      })
      .pipe(finalize(() => (this.userSearchLoading = false)))
      .subscribe({
        next: (response: any) => {
          this.userSearchResults = response.member || [];
          this.selectedUserIndex = this.userSearchResults.length > 0 ? 0 : -1;
        },
        error: (err) => {
          console.error('Erreur lors de la recherche des utilisateurs', err);
        },
      });
  }

  selectUser(user: any): void {
    const mention = `<span class="user-mention" data-user-id="${user.user}" contenteditable="false">@${user.fullName}</span>&nbsp;`;

    const range = window.getSelection()?.getRangeAt(0);
    if (range) {
      range.setStart(range.startContainer, this.lastAtPosition);
      range.deleteContents();

      const div = document.createElement('div');
      div.innerHTML = mention;
      const node = div.firstChild as Node;
      range.insertNode(node);

      range.setStartAfter(node);
      range.collapse(true);

      this.content = this.textarea.nativeElement.innerHTML;
      this.contentChange.emit(this.content);

      // Émettre la liste des mentions mises à jour
      this.emitMentions();
    }

    this.showUserDropdown = false;
    this.lastAtPosition = -1;
    this.selectedUserIndex = -1;
  }

  private emitMentions(): void {
    if (!this.textarea?.nativeElement) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(
      this.textarea.nativeElement.innerHTML,
      'text/html'
    );
    const mentionElements = doc.querySelectorAll('.user-mention');

    const mentions = Array.from(mentionElements).map((el) => ({
      id: el.getAttribute('data-user-id'),
      fullName: el.textContent?.replace('@', '') || '',
    }));

    this.mentionsChange.emit(mentions);
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    if (
      !this.textarea?.nativeElement.contains(event.target as Node) &&
      !(event.target as HTMLElement).closest('.user-mention-dropdown')
    ) {
      this.showUserDropdown = false;
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.showUserDropdown) {
      switch (event.key) {
        case 'ArrowUp':
          event.preventDefault();
          this.selectedUserIndex = Math.max(0, this.selectedUserIndex - 1);
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.selectedUserIndex = Math.min(
            this.userSearchResults.length - 1,
            this.selectedUserIndex + 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (
            this.selectedUserIndex >= 0 &&
            this.selectedUserIndex < this.userSearchResults.length
          ) {
            this.selectUser(this.userSearchResults[this.selectedUserIndex]);
          }
          break;
        case 'Escape':
          event.preventDefault();
          this.showUserDropdown = false;
          break;
      }
    }
  }

  clear(): void {
    this.content = '';
    if (this.textarea?.nativeElement) {
      this.textarea.nativeElement.innerHTML = '';
    }
  }
}
